import { useCallback, useContext, useState } from "react";
import SkeletonBase from "newui/components/Skeleton";
import { basePathAuth } from "utils/helpers";
import { navigate } from "@reach/router";
import { SubmissionSmartFieldPreviewContext } from "context/RFQSubmittedResponseContext";
import { SmartFieldCardButtons } from "pages/RFQ/RFQ/CreateRFQ/PreviewRFQ/Questionnaire/_components/SmartFieldCardButtons";
import { cn } from "utils/helpers";
import { ModalNew as Modal } from "components/Modal/ModalNew";
import { SmartFieldProjectEditor } from "components/RFQ/SmartFieldEditor/SmartFieldProjectEditor";
import { DEFAULT_IMAGE_BANNER } from "utils/constants";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { useProcurementMethodNamesSelected } from "newui/store/utils";

export const ProjectExperienceCardSkeleton = ({ rfqSubmission }) => {
	return (
		<div className="smart-field-scroll my-3 min-w-[410px] animate-pulse overflow-hidden rounded-md bg-gray-100 pb-6">
			<SkeletonBase
				width="100%"
				height="145px"
			/>
			<div className="space-y-6 px-6 py-2">
				<div className="mb-3 mt-10 flex items-center justify-start gap-3">
					<SkeletonBase width="160px" />
				</div>
				<div className="space-y-4 pb-2 pt-3">
					<SkeletonBase width="100%" />
					<SkeletonBase width="100%" />
					<SkeletonBase width="100%" />
					<SkeletonBase width="100%" />
				</div>
				{rfqSubmission && (
					<SkeletonBase
						width="100%"
						height="46px"
					/>
				)}
			</div>
		</div>
	);
};

export const ProjectExperienceCard = ({
	values,
	rfqSubmission,
	selected,
	selectHandler,
	companyId,
	isPreview,
	supplierSmartField,
	isSubmitting,
	className,
	isClientPreview,
}) => {
	const { rfxId } = useRfxSubmissionData();
	const submissionSmartFieldContext = useContext(
		SubmissionSmartFieldPreviewContext
	);
	const [isEditorOpen, setIsEditorOpen] = useState(false);
	const { selectedValuesCsv: procurementMethodsCsv } =
		useProcurementMethodNamesSelected(values?.procurementMethodIds);

	function formatCurrency(value) {
		if (value >= 1e6) {
			return (value / 1e6).toFixed(1) + "M";
		} else if (value >= 1e3) {
			return (value / 1e3).toFixed(1) + "K";
		} else if (value > 0) {
			return value.toFixed(1);
		} else {
			return " - ";
		}
	}

	const handleProjectExperienceOpen = () => {
		if (supplierSmartField && rfxId) {
			setIsEditorOpen(true);
		} else {
			navigate(
				`${basePathAuth(companyId)}company-profile/projects/edit-project/${values?.id}`
			);
		}
	};

	const handleViewClick = useCallback(() => {
		submissionSmartFieldContext.setModalDetail({
			data: values,
			type: "project",
		});
		submissionSmartFieldContext.setIsModalOpen(true);
	}, [submissionSmartFieldContext, values]);

	return (
		<>
			<div
				className={cn(
					"smart-field-scroll my-3 h-max min-w-[410px] overflow-hidden rounded-md bg-white pb-3 shadow-md",
					className
				)}
			>
				<div
					className="relative flex items-center justify-between"
					style={{ height: "145px" }}
				>
					<div className="absolute left-0 top-0 h-[6px] w-full bg-royal-blue2"></div>
					<div
						className="bg-cover bg-center"
						style={{
							width: "100%",
							height: "145px",
							backgroundImage: `url(${values?.images?.length > 0 ? values?.images[0] : `${process.env.PUBLIC_URL + DEFAULT_IMAGE_BANNER}`})`,
						}}
					></div>
				</div>
				<div className="space-y-6 pb-3 pl-5 pr-5 pt-3">
					<div>
						<h3 className="mt-8 line-clamp-1 overflow-hidden font-roboto text-[20px] font-semibold capitalize text-primary-900">
							{values?.name || " - "}
						</h3>
					</div>
					<div>
						<div
							className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
							style={{ height: "40px" }}
						>
							<div className="font-roboto text-[14px] font-normal text-primary-500">
								Construction Value
							</div>
							<div className="font-roboto text-[14px] font-semibold text-primary-900">
								${formatCurrency(values?.value)}
							</div>
						</div>
						<div
							className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
							style={{ height: "40px" }}
						>
							<div className="font-roboto text-[14px] font-normal text-primary-500">
								Completion
							</div>
							<div className="font-roboto text-[14px] font-semibold text-primary-900">
								{values?.year_completed || " - "}
							</div>
						</div>
						<div
							className="flex items-center justify-between space-x-0 border-b border-solid border-[#EAECF0]"
							style={{ height: "40px" }}
						>
							<div className="font-roboto text-[14px] font-normal text-primary-500">
								Client Name
							</div>
							<div className="font-roboto text-[14px] font-semibold text-primary-900">
								{values?.client_name || " - "}
							</div>
						</div>
						<div
							className="flex items-center justify-between space-x-0"
							style={{ height: "40px" }}
						>
							<div className="font-roboto text-[14px] font-normal text-primary-500">
								Procurement Model
							</div>
							<div className="font-roboto text-[14px] font-semibold text-primary-900">
								{procurementMethodsCsv || " - "}
							</div>
						</div>
					</div>
					{!isClientPreview && (
						<SmartFieldCardButtons
							rfqSubmission={rfqSubmission}
							selected={selected}
							selectHandler={() => selectHandler(values?.id)}
							isLoading={isSubmitting}
							editHandler={handleProjectExperienceOpen}
							editLabel="Project"
							isPreview={isPreview}
							viewHandler={handleViewClick}
							supplierSmartField={supplierSmartField}
							dataTestId={`select-project-${values?.id}`}
						/>
					)}
				</div>
			</div>
			{isEditorOpen && (
				<Modal
					isOpen={isEditorOpen}
					onClose={() => setIsEditorOpen(false)}
					headerContent={`Edit ${values?.name}`}
				>
					<SmartFieldProjectEditor
						projectId={values.id}
						rfxId={rfxId}
						companyId={companyId}
						onRequestClose={() => setIsEditorOpen(false)}
					/>
				</Modal>
			)}
		</>
	);
};
